import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  cancelSubscriptionAPI,
  cancelUpcomingPlanAPI,
  changeSubscriptionPlanAPI,
  createSubscriptionAPI,
  createSubscriptionAddonAPI,
  getCurrentSubscriptionAPI,
  getCurrentSubscriptionInvoiceAPI,
  updatePaymentMethodAPI,
  subscriptionPaymentSuccessAPI,
  subscriptionVerifyPaymentAPI,
  verifySubscriptionAddonPaymentAPI,
} from "../../api/common/SubscriptionApi";
import { saveToken } from "../../../token/token";

const initialState = {
  currentSubscription: null,
  subscriptionInvoices: null,
  subscriptionPayment: null,
  loading: false,
  loading2: false,
  paymentLoading: false,
  paymentMethodLoading: false,
  subscriptionVerifyPayment: null,
  subscriptionAddonPayment: null,
  subscriptionAddonPaymentVerification: null,
};

export const createSubscription = createAsyncThunk(
  "subscription/create",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await createSubscriptionAPI(payload);
      return response.data;
    } catch (err) {
      return rejectWithValue({
        errorMessage: err?.message,
      });
    }
  },
);

export const changeSubscriptionPlan = createAsyncThunk(
  "subscription/change-plan",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await changeSubscriptionPlanAPI(payload);
      return response.data;
    } catch (err) {
      return rejectWithValue({
        errorMessage: err?.message,
      });
    }
  },
);

export const cancelSubscription = createAsyncThunk(
  "subscription/cancel",
  async (_, { rejectWithValue }) => {
    try {
      const response = await cancelSubscriptionAPI();
      return response.data;
    } catch (err) {
      return rejectWithValue({
        errorMessage: err?.message,
      });
    }
  },
);

export const cancelUpcomingPlan = createAsyncThunk(
  "subscription/cancel-upcoming-plan",
  async (_, { rejectWithValue }) => {
    try {
      const response = await cancelUpcomingPlanAPI();
      return response.data;
    } catch (err) {
      return rejectWithValue({
        errorMessage: err?.message,
      });
    }
  },
);

export const subscriptionVerifyPayment = createAsyncThunk(
  "subscription/verify-payment",
  async (_, { rejectWithValue }) => {
    try {
      const response = await subscriptionVerifyPaymentAPI();
      return response.data;
    } catch (err) {
      return rejectWithValue({
        errorMessage: err?.message,
      });
    }
  },
);

export const updatePaymentMethod = createAsyncThunk(
  "subscription/update-payment-method",
  async (_, { rejectWithValue }) => {
    try {
      const response = await updatePaymentMethodAPI();
      return response.data;
    } catch (err) {
      return rejectWithValue({
        errorMessage: err?.message,
      });
    }
  },
);

export const createSubscriptionAddon = createAsyncThunk(
  "subscription/create-addon",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await createSubscriptionAddonAPI(payload);
      return response.data;
    } catch (err) {
      return rejectWithValue({
        errorMessage: err?.message,
      });
    }
  },
);

export const verifySubscriptionAddonPayment = createAsyncThunk(
  "subscription/verify-addon-payment",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await verifySubscriptionAddonPaymentAPI(payload);
      return response.data;
    } catch (err) {
      return rejectWithValue({
        errorMessage: err?.message,
      });
    }
  },
);

export const getCurrentSubscriptionInvoice = createAsyncThunk(
  "subscription/get-current-invoice",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await getCurrentSubscriptionInvoiceAPI(payload);
      return response.data;
    } catch (err) {
      return rejectWithValue({
        errorMessage: err?.message,
      });
    }
  },
);

export const getCurrentSubscription = createAsyncThunk(
  "subscription/get-current-subscription",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getCurrentSubscriptionAPI();
      return response.data;
    } catch (err) {
      return rejectWithValue({
        errorMessage: err?.message,
      });
    }
  },
);

export const subscriptionPaymentSuccess = createAsyncThunk(
  "subscription/payment-success",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await subscriptionPaymentSuccessAPI(payload);
      return response.data;
    } catch (err) {
      return rejectWithValue({
        errorMessage: err?.message,
      });
    }
  },
);

const subscriptionSlice = createSlice({
  name: "subscription",
  initialState,
  extraReducers: (builder) => {
    builder.addCase(createSubscription.pending, (state) => {
      state.paymentLoading = true;
    });
    builder.addCase(createSubscription.fulfilled, (state, action) => {
      state.currentSubscription = action.payload?.response;
      state.paymentLoading = true;
    });
    builder.addCase(createSubscription.rejected, (state) => {
      state.paymentLoading = false;
    });

    builder.addCase(changeSubscriptionPlan.pending, (state) => {
      state.paymentLoading = true;
    });
    builder.addCase(changeSubscriptionPlan.fulfilled, (state, action) => {
      state.currentSubscription = action.payload?.response;
      state.paymentLoading = true;
    });
    builder.addCase(changeSubscriptionPlan.rejected, (state) => {
      state.paymentLoading = false;
    });

    builder.addCase(cancelSubscription.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(cancelSubscription.fulfilled, (state, action) => {
      state.currentSubscription =
        action.payload?.response || state.currentSubscription;
      state.loading = false;
    });
    builder.addCase(cancelSubscription.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(cancelUpcomingPlan.pending, (state) => {
      state.loading2 = true;
    });
    builder.addCase(cancelUpcomingPlan.fulfilled, (state, action) => {
      state.currentSubscription =
        action.payload?.response || state.currentSubscription;
      state.loading2 = false;
    });
    builder.addCase(cancelUpcomingPlan.rejected, (state) => {
      state.loading2 = false;
    });

    builder.addCase(updatePaymentMethod.pending, (state) => {
      state.paymentMethodLoading = true;
    });
    builder.addCase(updatePaymentMethod.fulfilled, (state, action) => {
      state.currentSubscription =
        action.payload?.response || state.currentSubscription;
      state.paymentMethodLoading = false;
    });
    builder.addCase(updatePaymentMethod.rejected, (state) => {
      state.paymentMethodLoading = false;
    });

    builder.addCase(getCurrentSubscriptionInvoice.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(
      getCurrentSubscriptionInvoice.fulfilled,
      (state, action) => {
        state.subscriptionInvoices = action.payload?.response;
        state.loading = false;
      },
    );
    builder.addCase(getCurrentSubscriptionInvoice.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(getCurrentSubscription.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getCurrentSubscription.fulfilled, (state, action) => {
      state.currentSubscription = action.payload?.response;
      state.paymentLoading = false;
      state.loading = false;
    });
    builder.addCase(getCurrentSubscription.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(subscriptionPaymentSuccess.pending, (state) => {
      state.paymentLoading = true;
    });
    builder.addCase(subscriptionPaymentSuccess.fulfilled, (state, action) => {
      state.subscriptionPayment = action.payload?.response;
      state.paymentLoading = false;
    });
    builder.addCase(subscriptionPaymentSuccess.rejected, (state) => {
      state.paymentLoading = false;
    });

    builder.addCase(subscriptionVerifyPayment.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(subscriptionVerifyPayment.fulfilled, (state, action) => {
      state.subscriptionVerifyPayment = action.payload?.response;
      state.loading = false;
    });
    builder.addCase(subscriptionVerifyPayment.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(createSubscriptionAddon.pending, (state) => {
      state.paymentLoading = true;
    });
    builder.addCase(createSubscriptionAddon.fulfilled, (state, action) => {
      state.subscriptionAddonPayment = action.payload?.response;
      state.paymentLoading = false;
    });
    builder.addCase(createSubscriptionAddon.rejected, (state) => {
      state.paymentLoading = false;
    });

    builder.addCase(verifySubscriptionAddonPayment.pending, (state) => {
      state.paymentLoading = true;
    });
    builder.addCase(
      verifySubscriptionAddonPayment.fulfilled,
      (state, action) => {
        state.subscriptionAddonPaymentVerification = action.payload?.response;
        console.log(
          "action.payload?.response?.token ",
          action.payload?.response,
        );
        saveToken(action.payload?.response?.token || "");
        state.paymentLoading = false;
      },
    );
    builder.addCase(verifySubscriptionAddonPayment.rejected, (state) => {
      state.paymentLoading = false;
    });
  },
});

export default subscriptionSlice.reducer;
